/**
 * Time Ago Pipe
 * 
 * Transforms a timestamp into a human-readable relative time string.
 * E.g., "5 minutes ago", "2 hours ago", "yesterday", "3 days ago"
 * 
 * @usage
 * {{ notification.createdAt | timeAgo }}
 */

import { Pipe, PipeTransform } from '@angular/core';
import { Timestamp } from 'firebase/firestore';

@Pipe({
    name: 'timeAgo',
    standalone: true,
    pure: false // Not pure so it updates with time
})
export class TimeAgoPipe implements PipeTransform {
    transform(value: Timestamp | Date | { seconds: number; nanoseconds: number } | null | undefined): string {
        if (!value) {
            return '';
        }
        
        // Convert to Date
        let date: Date;
        
        if (value instanceof Date) {
            date = value;
        } else if (value instanceof Timestamp || (typeof value === 'object' && 'seconds' in value)) {
            // Firebase Timestamp or Timestamp-like object
            date = new Date(value.seconds * 1000);
        } else {
            return '';
        }
        
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
        
        // Future dates
        if (seconds < 0) {
            return 'just now';
        }
        
        // Less than a minute
        if (seconds < 60) {
            return 'just now';
        }
        
        // Less than an hour
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) {
            return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
        }
        
        // Less than a day
        const hours = Math.floor(minutes / 60);
        if (hours < 24) {
            return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
        }
        
        // Less than a week
        const days = Math.floor(hours / 24);
        if (days < 7) {
            if (days === 1) {
                return 'yesterday';
            }
            return `${days} days ago`;
        }
        
        // Less than a month
        const weeks = Math.floor(days / 7);
        if (weeks < 4) {
            return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
        }
        
        // Less than a year
        const months = Math.floor(days / 30);
        if (months < 12) {
            return months === 1 ? '1 month ago' : `${months} months ago`;
        }
        
        // More than a year
        const years = Math.floor(days / 365);
        return years === 1 ? '1 year ago' : `${years} years ago`;
    }
}
